import { useEffect, useMemo, useState } from "react";
import { api, imgUrl } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

export default function PortfolioPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const collections = useMemo(() => [...new Set(items.map((i) => i.collection || "General"))], [items]);
  const displayed = filter ? items.filter((i) => (i.collection || "General") === filter) : items;

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="portfolio-page">
      <Seo title="Portfolio — Anaya Fashion & Gifts" description="A gallery of moments styled by Anaya Fashion & Gifts — our pieces, parcels and celebrations." path="/portfolio" />
      <Reveal>
        <div className="text-center">
          <p className="text-[11px] tracking-[0.4em] text-primary uppercase">Portfolio</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl">Moments we&#8217;ve <em className="gold-text">styled</em></h1>
          <p className="mx-auto mt-4 max-w-md text-sm font-light text-muted-foreground">A little gallery of our pieces, parcels and happy celebrations.</p>
        </div>
      </Reveal>

      {collections.length > 1 && (
        <div className="mt-8 flex flex-wrap justify-center gap-2" data-testid="portfolio-filters">
          <button data-testid="portfolio-filter-all" onClick={() => setFilter("")}
            className={`rounded-full px-5 py-2 text-xs tracking-[0.15em] uppercase transition-colors ${!filter ? "bg-foreground text-white" : "border border-input hover:bg-secondary"}`}>All</button>
          {collections.map((c) => (
            <button key={c} data-testid={`portfolio-filter-${c}`} onClick={() => setFilter(c)}
              className={`rounded-full px-5 py-2 text-xs tracking-[0.15em] uppercase transition-colors ${filter === c ? "bg-foreground text-white" : "border border-input hover:bg-secondary"}`}>{c}</button>
          ))}
        </div>
      )}

      {displayed.length === 0 ? (
        <p className="glass mx-auto mt-10 max-w-md rounded-3xl p-10 text-center text-sm font-light text-muted-foreground" data-testid="portfolio-empty">
          Our portfolio is being curated — check back soon!
        </p>
      ) : (
        <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4" data-testid="portfolio-grid">
          {displayed.map((it, i) => (
            <Reveal key={it.id} delay={i * 0.05}>
              <figure className="group overflow-hidden rounded-3xl shadow-sm ring-1 ring-white/70" data-testid={`portfolio-item-${it.id}`}>
                <div className="relative overflow-hidden">
                  <img src={imgUrl(it.url)} alt={it.title || "Anaya portfolio"} loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  {(it.title || it.description) && (
                    <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/70 to-transparent p-4">
                      {it.title && <p className="font-display text-lg leading-tight text-white">{it.title}</p>}
                      {it.description && <p className="mt-0.5 line-clamp-2 text-xs font-light text-white/85">{it.description}</p>}
                      <p className="mt-1 text-[9px] tracking-[0.25em] text-white/70 uppercase">{it.collection}</p>
                    </figcaption>
                  )}
                </div>
              </figure>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
